import { Input, Tooltip, TreeSelect } from "antd-v5";
import { FormInstance } from "antd/lib/form";
import { InfoIcon } from "components/Icons";
import { getVocabulary } from "components/Vocabulary";
import { FeatureFlagContentValuesType } from "contexts/FeatureFlagContext";
import {
  compact,
  isArray,
  isEmpty,
  isNil,
  isNull,
  isUndefined,
  map,
  split,
  uniqBy,
} from "lodash";
import moment from "moment";
import React, { MutableRefObject } from "react";
import TicketsCreationAPI from "restAPI/TicketsCreationAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { Rule } from "turndown";
import { disablePastDates } from "utils/dateUtils";
import {
  ALL_DATE_FIELDS,
  CUSTOM_FIELDS_SINGLE_SELECTION,
  CUSTOM_FIELDS_WITH_OPTIONS,
  CUSTOM_FIELDS_WITH_SEARCH,
  CUSTOM_FIELDS_WITH_USER_API,
  CUSTOM_FIELD_TYPE,
  FieldTypes,
  PROVIDE_SELECTED_VALUES,
  TIME_SUPPORTED_DATE_FIELDS,
} from "utils/formUtils";

import { findingFieldValidator } from "../TicketTemplateForm";
import { parseAreaOptions } from "../azureBoard/AzureBoardMappingForm";
import { CUSTOM_FIELDS_RISK_RATING_MAPPING_KEYS } from "../genericIntegrationTicketConstants";
import { stripHtml } from "../genericIntegrationTicketUtils";
import { CustomFieldV2 } from "./CustomFieldsV2";
import {
  CustomField,
  CustomFieldStateMap,
  CustomFields,
  InitialValues,
} from "./types";

export const checkEmptyField = (value: unknown) =>
  isUndefined(value) || isNull(value);

export const getNoCustomFieldsMessage = (
  initialValues: InitialValues,
  loginConfigId?: number,
  ticketConfigurationId?: number,
) => {
  const message =
    !loginConfigId ||
    !ticketConfigurationId ||
    initialValues.projectKey ||
    initialValues.issueType
      ? "Please select Project Key and Issue Type"
      : "Does not have any custom fields configured for this project";
  return !loginConfigId || !initialValues.projectKey || !initialValues.issueType
    ? message
    : "No Custom Fields.";
};

export const getCollapseProps = (tooltip: string | undefined) => ({
  type: "info",
  Key: "custom_field",
  defaultActiveKey: "custom_field",
  props: {
    destroyInactivePanel: true,
  },
  header: (
    <>
      Configure Custom Fields{" "}
      <Tooltip
        title={
          tooltip ||
          "By enabling the toggles, only those fields will be mapped to your project and the rest will be ignored. A text area and drop-down boxes allow you to add default values. These values will be considered every time you create a ticket."
        }
      >
        {" "}
        <InfoIcon />
      </Tooltip>
    </>
  ),
});

export const pullCustomFields = () => {};

export const alterCustomFieldsAPIResponse = ({
  fields,
  form,
  initialData,
  useValueField,
  customFieldsValueRef,
  featureFlags,
  innerRef = false,
  toolName,
  projectKey,
  showFieldDefaultValue = true,
  skipDefaultValue = false,
}: {
  fields: CustomFields;
  form: FormInstance;
  initialData: MutableRefObject<InitialValues>;
  useValueField: boolean;
  customFieldsValueRef: MutableRefObject<Map<string, any>>;
  featureFlags: FeatureFlagContentValuesType;
  innerRef?: boolean;
  toolName: string;
  projectKey: string;
  showFieldDefaultValue?: boolean;
  skipDefaultValue?: boolean;
}) => {
  const customState: CustomFieldStateMap = {};
  if (isArray(fields)) {
    fields.forEach((field) => {
      Object.assign(field, { active: field.isRequired ? true : field.active });
      // set false for null boolean type values
      // required field throws validation error
      // when user tries to save value initially
      // without changing the checkbox
      if (
        field.isRequired &&
        field.type === "boolean" &&
        isNil(field.defaultVal)
      ) {
        form.setFieldsValue({ [field.key]: false });
      }
      if (field.active) {
        customState[field.key] = field.active;
        // if field.defaultVal exist, set form field value
        // defaultVale only need to show on integration mapping form not on runbook
        // this is behaviour is required for duedate type field
        if (field.defaultVal && showFieldDefaultValue) {
          form.setFieldsValue({ [field.key]: field.defaultVal });
        }
      }
    });
    if (!isEmpty(initialData.current?.customFields)) {
      fields.forEach((customField) => {
        const obj = initialData.current.customFields.find(
          (f: CustomField) => f.key === customField.key,
        );
        if (obj) {
          const value = useValueField ? obj.value || "" : obj.defaultVal || "";
          Object.assign(customField, {
            isMapped: useValueField
              ? !isEmpty(obj.value)
              : !isEmpty(obj.defaultVal),
          });
          // check type in lowercase, since JIRA has datetime and Azure has dateTime
          const typeLowerCase = obj.type?.toLowerCase();
          const isTextField =
            typeLowerCase === "textarea" ||
            typeLowerCase === "string" ||
            typeLowerCase === "html" ||
            isUndefined(typeLowerCase);

          let fieldValue =
            ALL_DATE_FIELDS.indexOf(typeLowerCase) === -1 &&
            !isTextField &&
            value.includes(",")
              ? compact(value.split(","))
              : value;

          Object.assign(customField, {
            ...(!skipDefaultValue
              ? {
                  defaultValue: isEmpty(customField.defaultValue)
                    ? { id: fieldValue, name: fieldValue }
                    : customField.defaultValue,
                }
              : {}),
            active: innerRef ? obj.active : customField.active,
          });
          if (customField.active) {
            customState[customField.key] = customField.active;
          }

          if (
            (typeLowerCase === "datetime" ||
              ALL_DATE_FIELDS.indexOf(obj.type) !== -1) &&
            fieldValue &&
            typeof fieldValue === "string"
          ) {
            fieldValue = moment(fieldValue).isValid()
              ? moment(fieldValue)
              : fieldValue;
          }

          if (obj.type === "boolean" && typeof fieldValue === "string") {
            fieldValue = fieldValue === "true";
          }

          let _value = fieldValue;
          if (
            // @ts-expect-error - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ select: string; multiselect: string; priority: string; cascadingselect: string; array: string; multiuserpicker: string; textarea: string; html: string; userpicker: string; multicheckboxes: string; ... 33 more ...; duedate: string; }'.
            CUSTOM_FIELD_TYPE[customField.type] &&
            ["option", "array", "any"].includes(customField.dataType)
          ) {
            _value = isEmpty(fieldValue) ? [] : fieldValue;
          }

          if (customField.type === "cascadingselect") {
            _value = split(fieldValue, ":");
          }

          if (customFieldsValueRef) {
            customFieldsValueRef.current = {
              ...customFieldsValueRef.current,
              ...{ [customField.key]: _value },
            };
          }
          //get and set severity/priority form field values from custom field mappings object
          // based on the customField.defaultVal
          if (featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING) {
            const isRiskRatingField =
              customField.defaultVal &&
              customField.defaultVal.indexOf("finding.riskRating") !== -1;
            const isSeverityValueField =
              customField.defaultVal &&
              customField.defaultVal.indexOf("finding.severity.value") !== -1;
            if (isRiskRatingField && !isEmpty(customField.mappings)) {
              for (const riskTitleKey of CUSTOM_FIELDS_RISK_RATING_MAPPING_KEYS) {
                if (customField.mappings[getVocabulary(riskTitleKey)]) {
                  form.setFieldsValue({
                    [`${customField.key}-${getVocabulary(riskTitleKey)}`]:
                      customField.mappings[getVocabulary(riskTitleKey)],
                  });
                }
              }
            }

            if (isSeverityValueField && !isEmpty(customField.mappings)) {
              for (const mappingKey in customField.mappings) {
                form.setFieldsValue({
                  [`${customField.key}-${mappingKey}`]:
                    customField.mappings[mappingKey],
                });
              }
            }
          }
          form.setFieldsValue({ [customField.key]: _value });

          // TODO: move this to AzureBoard custom fields file, once created
          // when project key or issue type changes
          // for Azure board don't set custom field value if it is an Area or Iteration field
          if (
            toolName === "Azure Board" &&
            (customField.name === "Area" ||
              customField.name === "Iterations") &&
            initialData.current.projectKey !== projectKey
          ) {
            form.setFieldsValue({ [customField.key]: undefined });
          }
        }
      });
    }
  }
  return { customState, fields };
};

export const generateCustomFields = ({
  fields,
  projectKey,
  placeholder,
  loginConfigId,
  initialValues,
  featureFlags,
  getApiParams,
  customFieldStateRef,
}: {
  fields: CustomFields;
  projectKey: string;
  placeholder?: string;
  loginConfigId: number;
  initialValues: InitialValues;
  featureFlags: FeatureFlagContentValuesType;
  customFieldStateRef: MutableRefObject<CustomFieldStateMap>;
  getApiParams: (
    loginConfigId: number,
    projectKey: string,
    fieldLabel: string,
    type?: string,
  ) => any;
}): Array<CustomFieldV2> => {
  const customFields = fields.map((customField) => {
    const isEnabled =
      customFieldStateRef.current?.[customField.key] ?? customField.active;

    const rules = [];
    if (
      customField.dataType === "number" ||
      ["integer", "float", "double", "decimal"].indexOf(customField.type) !== -1
    ) {
      rules.push({
        validator: (_rule: Rule, value: string) => {
          if (value) {
            if (value.indexOf("${") !== -1) {
              return findingFieldValidator(_rule, value);
            } else {
              return !isNaN(+value)
                ? Promise.resolve()
                : Promise.reject("Not a number");
            }
          }
          return Promise.resolve();
        },
      });
    }

    const field: any = {
      name: customField.key,
      title: customField.name,
      disableVariableSelection: customField.disableVariableSelection,
      // Auto-show mapped fields only when they are active on the new UI.
      isMapped:
        (customField.isMapped || !isEmpty(customField.defaultVal)) &&
        (customField.isRequired || isEnabled),
      requireField: customField.isRequired, //useed requireField ,since required key validation from form utils
      rules: [
        {
          required: customField.isRequired || isEnabled,
          message: <>{customField.name} is required</>,
        },
        ...rules,
      ],
      // check lowercase too, since JIRA has datetime where as Azure has dateTime
      type:
        // @ts-expect-error - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type
        CUSTOM_FIELD_TYPE[customField.type] ||
        // @ts-expect-error - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type
        CUSTOM_FIELD_TYPE[customField.type.toLowerCase()],
      placeholder: placeholder || customField.name,
    };

    // Textarea
    if (field.type === FieldTypes.TEXT_AREA) {
      field.NoDefaultLengthRule = true;
    }

    // Multiselect
    if (CUSTOM_FIELDS_WITH_OPTIONS.includes(customField.type)) {
      field.options = customField.allowedValues;
      if (
        customField.type === "multicheckboxes" ||
        customField.type === "radiobuttons"
      ) {
        field.options = field.options.map((option: any) => {
          return {
            label: <span title={option.name}>{option.name}</span>,
            value: option.id,
            ...option,
          };
        });
      }
      if (PROVIDE_SELECTED_VALUES.includes(customField.type)) {
        field.props = {
          ...(field.props || {}),
          labelInValue: true,
        };
      }
    }

    // Tree Select
    if (customField.name === "Area" || customField.name === "Iterations") {
      field.render = () => (
        <TreeSelect
          showSearch
          dropdownStyle={{
            maxHeight: 400,
          }}
          style={{ width: "80%" }}
          allowClear
          treeData={parseAreaOptions(customField.allowedValues)}
          virtual={false}
          treeDefaultExpandAll
        />
      );
    }

    if (CUSTOM_FIELDS_WITH_SEARCH.includes(customField.type)) {
      const isReferenceType = customField.type === "reference";
      if (
        (isReferenceType && !isEmpty(customField.dataType)) ||
        !isReferenceType
      ) {
        if (isReferenceType) {
          field.type = FieldTypes.SELECT_WITH_PAGINATED_DATA;
        }

        field.props = {
          mode: CUSTOM_FIELDS_SINGLE_SELECTION.includes(customField.type)
            ? "single"
            : "multiple",
          parseSelectOptions: (options: any) => {
            if (!isEmpty(customField.selectedValues)) {
              options = uniqBy(
                [...options, ...customField.selectedValues],
                "id",
              );
            }
            options = stripHtml(options);
            return options;
          },
          api: TicketsCreationAPI.getCustomTicketFieldsSuggestions,
          apiParams: getApiParams(
            loginConfigId,
            projectKey,
            isReferenceType ? customField.dataType : customField.name,
            customField.type,
          ),
          labelInValue: true,
        };
      }
    }
    if (CUSTOM_FIELDS_WITH_USER_API.includes(customField.type)) {
      field.props = {
        ...(customField.type === "multiuserpicker" && { mode: "multiple" }),
        apiParams: getApiParams(
          loginConfigId,
          projectKey,
          customField.name,
          customField.type,
        ),
        api: ToolsIntegrationsAPI.getTicketingUsers,
        parseSelectOptions: (options: any) => {
          if (!isEmpty(customField.selectedValues)) {
            options = uniqBy([...options, ...customField.selectedValues], "id");
          }
          return map(options, (o) => ({ label: o.name, value: o.id }));
        },
        labelInValue: true,
      };
    }
    // check type in lowercase, since JIRA has datetime and Azure has dateTime
    if (
      customField.type.toLowerCase() === "datetime" ||
      customField.type === "datepicker"
    ) {
      field.props = {
        disabledDate: disablePastDates,
      };
    }

    if (TIME_SUPPORTED_DATE_FIELDS.indexOf(customField.type) !== -1) {
      field.props = {
        showTime: true,
        disabledDate: disablePastDates,
      };
    }

    // check type in lowercase, since JIRA has datetime and Azure has dateTime
    if (
      !featureFlags.UNIFIED_TICKET_TEMPLATE &&
      (customField.type === "datepicker" ||
        customField.type.toLowerCase() === "datetime") &&
      !isEmpty(initialValues[customField.key]) &&
      !moment(initialValues[customField.key]).isValid()
    ) {
      field.type = undefined;
      field.render = () => (
        <Tooltip title="Template string is selected. Please clear template first to select date.">
          <span>
            <Input value={customField.defaultVal} disabled />
          </span>
        </Tooltip>
      );
    }

    if (customField.type === "HOUR_AND_MINUTE_STRING") {
      field.rules.push({
        validator: (rule: Rule, value: string) => {
          if (!isEmpty(value)) {
            if (!/^\d+h \d+m$/.test(value)) {
              return Promise.reject("Valid format for Planned effort is 1h 2m");
            }
          }
          return Promise.resolve();
        },
      });
    }
    return field;
  });
  return customFields;
};
